import { useSearchParams } from 'react-router-dom';
import { useGroups } from '../../hooks/useGroups';
import { Button } from '../common/Button';

const PRESETS = [
  { key: '24h', label: '24h', hours: 24 },
  { key: '7d', label: '7d', hours: 24 * 7 },
  { key: '30d', label: '30d', hours: 24 * 30 },
  { key: '90d', label: '90d', hours: 24 * 90 },
  { key: '1y', label: '1y', hours: 24 * 365 },
];

/**
 * Presets resolve to explicit from/to here so the API only ever sees bounds,
 * while the preset key stays in the URL for legibility. Same useSearchParams
 * pattern as IncidentsPage, so a report is a shareable link.
 *
 * `query` is the react-query key, so it must be a pure function of the URL
 * and nothing else. A preset's "now" is therefore floored to the minute: a
 * raw `new Date()` differs on every render, which makes every render a new
 * key, a new fetch and a new render -- an unbounded request loop that ends
 * only when the API rate limiter returns 429. The server aggregates hourly,
 * so the minute is already far finer than the data.
 */
export function useReportParams() {
  const [params, setParams] = useSearchParams();
  const scope = params.get('scope') ?? 'all';
  const rangeKey = params.get('range') ?? '30d';
  const compare = params.get('compare') === 'previous';

  const preset = PRESETS.find(p => p.key === rangeKey);
  const to = params.get('to') ?? new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString();
  const from = params.get('from')
    ?? new Date(Date.parse(to) - (preset?.hours ?? 720) * 3_600_000).toISOString();

  function set(key: string, value: string | null) {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === null) next.delete(key);
      else next.set(key, value);
      // A preset and explicit bounds are mutually exclusive.
      if (key === 'range') { next.delete('from'); next.delete('to'); }
      return next;
    }, { replace: true });
  }

  return {
    scope, rangeKey, compare, from, to,
    query: { scope, from, to, ...(compare ? { compare: 'previous' as const } : {}) },
    setScope: (v: string) => set('scope', v),
    setRange: (v: string) => set('range', v),
    toggleCompare: () => set('compare', compare ? null : 'previous'),
  };
}

/**
 * `hideScope` drops the fleet scope <select> for pages that report on a single,
 * already-identified service (the scorecard) — scope genuinely does not apply
 * there, but range presets and "vs previous" still do. See CRITICAL 1 in the
 * reporting review: the scorecard must not lose range/compare just because it
 * doesn't need scope.
 */
export function ScopeRangePicker({ hideScope = false }: { hideScope?: boolean }) {
  const { scope, rangeKey, compare, setScope, setRange, toggleCompare } = useReportParams();
  const { data: groups } = useGroups();

  return (
    <div className="flex flex-wrap items-center gap-3">
      {!hideScope && (
        <select
          value={scope}
          onChange={e => setScope(e.target.value)}
          aria-label="Report scope"
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
        >
          <option value="all">All services</option>
          {groups?.map(g => <option key={g.id} value={`group:${g.id}`}>Group: {g.name}</option>)}
        </select>
      )}

      <div className="flex gap-1" role="group" aria-label="Time range">
        {PRESETS.map(p => (
          <Button
            key={p.key}
            size="sm"
            variant={rangeKey === p.key ? 'primary' : 'secondary'}
            aria-pressed={rangeKey === p.key}
            onClick={() => setRange(p.key)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <Button size="sm" variant={compare ? 'primary' : 'secondary'} aria-pressed={compare} onClick={toggleCompare}>
        vs previous
      </Button>
    </div>
  );
}
